import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useParams, useNavigate } from 'react-router-dom';
import { TrophyIcon, AwardIcon, ClockIcon, ArrowLeftIcon, LogOutIcon, PrinterIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import learningLevelsData from '../../data/learningLevels.json';

const CompetitionLeaderboard = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  const { competitionId } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [competition, setCompetition] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  // Handle logout
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  // Fetch leaderboard data
  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      // Fetch competition details
      const competitionResponse = await api.get(`/competitions/${competitionId}`);
      if (competitionResponse.data.success) {
        setCompetition(competitionResponse.data.data);
      }

      // Fetch leaderboard
      const leaderboardResponse = await api.get(`/competitions/${competitionId}/leaderboard?page=1&limit=500`);
      if (leaderboardResponse.data.success) {
        setLeaderboard(leaderboardResponse.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      toast.error(t('competitions.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionId]);

  const translateLevelName = (levelName) => {
    if (!levelName) return '';
    const translations = learningLevelsData[levelName] || {};
    return translations[currentLanguage] || levelName;
  };

  // Get medal color
  const getMedalColor = (rank) => {
    if (rank === 1) return 'text-yellow-500';
    if (rank === 2) return 'text-gray-400';
    if (rank === 3) return 'text-green-700';
    return 'text-gray-600';
  };

  // Get row background
  const getRowBackground = (rank) => {
    if (rank === 1) return 'bg-yellow-50';
    if (rank === 2) return 'bg-gray-50';
    if (rank === 3) return 'bg-green-50';
    return 'bg-white';
  };
  
  // Format student name
  const getStudentName = (participant) => {
    return `${participant.firstName || ''} ${participant.lastName || ''}`.trim() || participant.username;
  };

  // Format time (minutes:seconds:centiseconds)
  const formatTime = (milliseconds) => {
    if (!milliseconds) return '00:00:00';
    
    const totalMs = parseInt(milliseconds);
    const minutes = Math.floor(totalMs / 60000);
    const seconds = Math.floor((totalMs % 60000) / 1000);
    const centiseconds = Math.floor((totalMs % 1000) / 10);
    
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}:${String(centiseconds).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!competition && leaderboard.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <p className="text-gray-600">{t('competitions.notFound')}</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .print\\:hidden {
            display: none !important;
          }
          .bg-gradient-to-br {
            background: white !important;
          }
          table {
            page-break-inside: auto;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          thead {
            display: table-header-group;
          }
          .shadow-lg, .shadow-md, .shadow-xl, .shadow-2xl {
            box-shadow: none !important;
          }
          @page {
            margin: 1cm;
          }
        }
      `}</style>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="py-3 px-6 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          {/* Navigation Buttons */}
          <div className={`flex items-center justify-between mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button
              onClick={() => navigate(-1)}
              className={`flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <ArrowLeftIcon className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
              {t('common.back')}
            </button>
            
            <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button
                onClick={handlePrint}
                className={`flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors print:hidden ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                <PrinterIcon className="w-4 h-4" />
                {t('common.print')}
              </button>
              
              <button
                onClick={handleLogout}
                className={`flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors print:hidden ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                <LogOutIcon className="w-4 h-4" />
                {t('common.logout')}
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
              <TrophyIcon className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{competition?.name || t('competitions.competition')}</h1>
              <p className="text-xs text-gray-600">{t('competitions.competitionRanking')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        {/* Top 3 Cards */}
        {leaderboard.length >= 3 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4 mb-6 md:py-6">
            {/* 2nd Place */}
            <div className="order-2 md:order-1">
              <div className="bg-white rounded-xl shadow-lg px-4 py-7 text-center transform hover:scale-105 transition-all duration-300 sm:py-8">
                <div className="flex justify-center mb-2">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                    <AwardIcon className="w-10 h-10 text-gray-400" />
                  </div>
                </div>
                <div className="text-4xl font-bold text-gray-400 mb-1">2</div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{getStudentName(leaderboard[1])}</h3>
                <p className="text-xs text-gray-500 mb-1">{translateLevelName(leaderboard[1].levelName) || '—'}</p>
                <p className="text-2xl font-bold text-primary-600">{leaderboard[1].totalScore}</p>
                <p className="text-xs text-gray-600">{t('competitions.score')}</p>
              </div>
            </div>

            {/* 1st Place */}
            <div className="order-1 md:order-2">
              <div className="bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl shadow-2xl px-5 py-8 text-center transform md:scale-105 md:-mt-2 transition-all duration-300 sm:py-9">
                <div className="flex justify-center mb-2">
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <TrophyIcon className="w-12 h-12 text-yellow-600" />
                  </div>
                </div>
                <div className="text-5xl font-bold text-white mb-1">1</div>
                <h3 className="text-xl font-bold text-white mb-1">{getStudentName(leaderboard[0])}</h3>
                <p className="text-xs text-yellow-100 mb-1">{translateLevelName(leaderboard[0].levelName) || '—'}</p>
                <p className="text-3xl font-bold text-white">{leaderboard[0].totalScore}</p>
                <p className="text-xs text-yellow-100">{t('competitions.score')}</p>
              </div>
            </div>

            {/* 3rd Place */}
            <div className="order-3">
              <div className="bg-white rounded-xl shadow-lg px-4 py-7 text-center transform hover:scale-105 transition-all duration-300 sm:py-8">
                <div className="flex justify-center mb-2">
                  <div className="w-16 h-16 bg-green-200 rounded-full flex items-center justify-center">
                    <AwardIcon className="w-10 h-10 text-green-700" />
                  </div>
                </div>
                <div className="text-4xl font-bold text-green-700 mb-1">3</div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{getStudentName(leaderboard[2])}</h3>
                <p className="text-xs text-gray-500 mb-1">{translateLevelName(leaderboard[2].levelName) || '—'}</p>
                <p className="text-2xl font-bold text-primary-600">{leaderboard[2].totalScore}</p>
                <p className="text-xs text-gray-600">{t('competitions.score')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Full Leaderboard Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-2xl font-bold text-gray-900">{t('competitions.competitionRanking')}</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                    {t('competitions.rank')}
                  </th>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                    {t('common.name')}
                  </th>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                    {t('competitions.rounds')}
                  </th>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                    {t('competitions.totalScore')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                    <div className="inline-block max-w-[10rem] overflow-hidden whitespace-nowrap text-ellipsis [direction:rtl] text-left">
                      <span className="inline-block [direction:ltr]">{t('competitions.totalTime')}</span>
                    </div>
                  </th>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                    {t('competitions.level')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {leaderboard.length > 0 ? (
                  leaderboard.map((participant, index) => (
                    <tr key={index} className={`hover:bg-gray-100 transition-colors ${getRowBackground(participant.rankPosition)}`}>
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className="flex items-center gap-3 justify-start">
                          {participant.rankPosition <= 3 ? (
                            <>
                              <AwardIcon className={`w-7 h-7 ${getMedalColor(participant.rankPosition)}`} />
                              <span className="text-lg font-bold" style={{ 
                                color: participant.rankPosition === 1 ? '#EAB308' : 
                                       participant.rankPosition === 2 ? '#9CA3AF' : 
                                       '#15803d' 
                              }}>
                                {participant.rankPosition}
                              </span>
                            </>
                          ) : (
                            <span className="text-lg font-semibold text-gray-600">{participant.rankPosition}</span>
                          )}
                        </div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className="text-sm font-medium text-gray-900">{getStudentName(participant)}</div>
                        <div className="text-xs text-gray-500">{participant.studentNumber}</div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {participant.completedRounds} / {competition.totalRounds}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        <span className="text-xl font-bold text-primary-600">{participant.totalScore}</span>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className="flex items-center gap-2">
                          <ClockIcon className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">{formatTime(participant.totalTimeMs)}</span>
                        </div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        <span className="text-sm text-gray-900">{translateLevelName(participant.levelName) || '—'}</span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      <TrophyIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p>{t('competitions.noStudents')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default CompetitionLeaderboard;

